// src/Payroll_Management/PayRoll_Report/PerformanceReportView.jsx
import { useEffect, useContext, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { ArrowLeft, Printer, RefreshCcw } from "lucide-react";

const BASE_URL = "https://nissi-india.com";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const PerformanceReportView = () => {
  const navigate = useNavigate();
  const q = useQuery();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = localStorage.getItem("finYearId") || "";
  const performId = q.get("performId") || "";

  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(false);

  const screenBadge = useMemo(() => "INFRA SCREEN : Performance View", []);

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  const fetchDetails = async () => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!String(performId).trim()) {
      toast.error("performId missing in URL.");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/employee-report/performance-view`, {
        params: {
          finYearId: Number(finYearId),
          performId: Number(performId),
        },
      });

      if (res?.data?.success) {
        setRow(res.data.row || null);
        if (!res.data.row) toast.info("Record not found");
      } else {
        toast.error(res?.data?.message || "Failed to load performance view");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetails();
    // eslint-disable-next-line
  }, [performId]);

  const handleBack = () => {
    // go back to listing page
    navigate(-1);
  };

  const handlePrint = () => {
  window.open(
    `/PayRoll/PerformanceReportViewPrint?performId=${encodeURIComponent(performId)}&autoPrint=1`,
    "_blank",
    "width=900,height=900,scrollbars=yes,resizable=yes"
  );
};


  return (
    <PayRollLayoutWrapper>
      <ToastContainer position="top-right" autoClose={2500} theme={isDark ? "dark" : "light"} />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">VIEW PERFORMANCE</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-4xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* Top */}
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <div>
              <div className="text-2xl font-extrabold">Performance Details</div>
              <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Perform ID: <b>{performId}</b>
              </div>

              <div className={`inline-block mt-2 px-4 py-2 rounded-2xl text-sm font-bold ${
                isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"
              }`}>
                {screenBadge}
              </div>
            </div>

            <div className="flex gap-3 flex-wrap">
              <button onClick={handleBack} className={ghostBtn}>
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>

              <button onClick={fetchDetails} className={ghostBtn} disabled={loading}>
                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>

              <button onClick={handlePrint} className={primaryBtn} disabled={!row}>
                <Printer className="w-4 h-4" />
                Print
              </button>
            </div>
          </div>

          {/* Card */}
          <div className={cardBase + " p-6"}>
            {loading ? (
              <div className="py-10 text-center">Loading...</div>
            ) : !row ? (
              <div className="py-10 text-center">No Record Found</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Employee Code" value={row.fld_Employeecode} />
                <Field label="Employee Name" value={row.fld_Employee} />
                <Field label="Designation" value={row.fld_designationname} />
                <Field label="Branch" value={row.fld_BranchName} />
                <Field label="Date" value={row.PerformanceDate} />
                <Field label="Title" value={row.fld_Title} full />
                <Field label="Details" value={row.fld_Details} full />
              </div>
            )}
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

function Field({ label, value, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <div className="text-sm font-semibold opacity-80">{label}</div>
      <div className="mt-2 px-4 py-3 rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-800/40">
        {value || ""}
      </div>
    </div>
  );
}

export default PerformanceReportView;
